import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Spinner from 'react-spinkit';
import { RootState } from '../../ApplicationState';
import { selectClassroom } from '../../Classrooms';
import { getGuidedReadingLevelRange, isUserHasKey } from '../../GlobalFunctions';
import { checkStudentEmailVerdict } from '../../BackendInterface';
import { EMAIL_CHECK_FAILED_ERROR, getStudentEmailVerdictError } from '../studentEmailVerdict';
import FormikControl from '../../components/FormikContainer/FormikControl';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { parseReadingLevel } from '@peekapak/common/lib/mappings';

interface Props {
  student: StudentProxyType;
  readingLevels: number[];
  onSubmit: (value: FormValues) => void | Promise<void>;
}

interface FormValues {
  name: string;
  readingLevel: string;
  email?: string;
}

const EMAIL_IN_USE_ERROR = 'This email is already associated with an existing login.';
const SUBMIT_FAILED_ERROR = "We couldn't update this student right now. Please try again.";

function isEmailConflict(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /email/i.test(message) && /(already|associated|exists|conflict|in use)/i.test(message);
}

const validationSchema = Yup.object({
  name: Yup.string().min(4, 'Student name must be at least 4 characters').required('Required'),
  readingLevel: Yup.string().required('Required'),
  email: Yup.string().email('Please enter a valid email address'),
});

const EditStudentEditControl = ({ student, readingLevels, onSubmit }: Props): JSX.Element => {
  const [isPending, setIsPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  let fullName = student.firstName;
  if (student.middleName) fullName += ' ' + student.middleName;
  if (student.lastName) fullName += ' ' + student.lastName;

  const normalizeEmail = (emailInput?: string) => (emailInput || '').trim().toLowerCase();
  const initialEmail = normalizeEmail(student.email);

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const currentReadAloudLevelScheme = classroom.readAloudLevelScheme || 'fountasAndPinnell';

  // Opens on what myPeekaville would actually confer, so the teacher is never
  // shown one level while the game plays another. Parsing rather than testing
  // for presence matters for students still carrying a pre-PE-1263 label: that
  // is a value, but not a readable level, and the backend already falls through
  // to the classroom for it.
  const getInitialReadingLevel = () => {
    const level = parseReadingLevel(student.readingLevel) ?? parseReadingLevel(classroom.readingLevel);
    return level !== undefined ? String(level) : '';
  };

  const initialValues: FormValues = {
    name: fullName,
    readingLevel: getInitialReadingLevel(),
    email: student.email || '',
  };

  // PE-1263: the option's value is the numeric reading level; only its label is
  // the scheme-specific range. Carrying the label as the value wrote strings
  // like 'Fountas and Pinnell K - L' into the database, which myPeekaville then
  // failed to parse back into a level.
  const getMappedReadingLevels = () => {
    return readingLevels.map((level) => ({
      key: getGuidedReadingLevelRange(level, currentReadAloudLevelScheme),
      value: String(level),
    }));
  };

  const isMiddleSchool = () => {
    if (classroom.grade && parseInt(classroom.grade) > 5) return true;
    return false;
  };

  return isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') ? (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setFieldError, setSubmitting }) => {
        setIsPending(true);
        const submittedEmail = normalizeEmail(values.email);
        if (submittedEmail && submittedEmail !== initialEmail) {
          try {
            // 'edit' rather than 'add': reassigning this student's address to
            // one another account already holds is a conflict, even when that
            // account is intact and in scope.
            const verdict = await checkStudentEmailVerdict(submittedEmail);
            const verdictError = getStudentEmailVerdictError(verdict, 'edit');

            if (verdictError) {
              setFieldError('email', verdictError);
              setSubmitting(false);
              setIsPending(false);
              return;
            }
          } catch {
            setFieldError('email', EMAIL_CHECK_FAILED_ERROR);
            setSubmitting(false);
            setIsPending(false);
            return;
          }
        }

        try {
          await Promise.resolve(onSubmit(values));
        } catch (error) {
          // The update can still be rejected by the backend after the
          // pre-flight check passes, for instance when the email was claimed in
          // between. Surface it on the field it belongs to rather than letting
          // Formik swallow the rejection.
          if (isEmailConflict(error)) {
            setFieldError('email', EMAIL_IN_USE_ERROR);
          } else {
            setSubmitError(SUBMIT_FAILED_ERROR);
          }
          setSubmitting(false);
          setIsPending(false);
          return;
        }
        // note: setIsPending(false) is intentionally omitted here — the modal
        // closes on success so the component unmounts; setting state on an
        // unmounted component is a no-op but suppresses React warnings.
      }}
    >
      {() => (
        <Form>
          <FormikControl
            control='input'
            type='text'
            name='name'
            label='Name'
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault()}
          />
          {!isMiddleSchool() && (
            <FormikControl
              control='select'
              label='Reading Level'
              name='readingLevel'
              options={getMappedReadingLevels()}
              onBlur={(event: React.ChangeEvent<HTMLSelectElement>) => event.preventDefault()}
            />
          )}
          <FormikControl
            control='input'
            type='email'
            name='email'
            label='School Email Address'
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault()}
          />
          {submitError && (
            <div role='alert' className='pkp-form-error'>
              {submitError}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <PrimaryButton type='submit' disabled={isPending}>
              {isPending ? 'Updating student...' : '+ Update Student'}
            </PrimaryButton>
            {isPending && (
              <div style={{ display: 'flex', alignItems: 'center', height: '1.6rem', overflow: 'hidden' }}>
                <Spinner
                  name='three-bounce'
                  style={{ transform: 'scale(0.4)', transformOrigin: 'left center', width: '4rem' }}
                />
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  ) : (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {() => (
        <Form>
          <FormikControl disabled control='input' type='text' name='name' label='Name' />
          <FormikControl disabled control='input' type='email' name='email' label='School Email Address' />
        </Form>
      )}
    </Formik>
  );
};

export default EditStudentEditControl;
